import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { LayoutChangeEvent } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import Animated, {
	useAnimatedStyle,
	withSpring,
	withRepeat,
	withSequence,
	useSharedValue,
	runOnJS,
	useDerivedValue,
	type SharedValue,
} from "react-native-reanimated";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { TextSkeleton } from "../ChatUI/TextSkeleton";
import { useAmplitudes } from "./useAmplitudes";
import type { AmplitudeMode } from "./useAmplitudes";

const BAR_WIDTH = 2;
const BAR_MARGIN = 1;
const MIN_HEIGHT = 2;
const MAX_HEIGHT = 25;

type WaveformBarProps = {
	amplitude: number;
	isPlaying: boolean;
	barWidth?: number;
	barMargin?: number;
	minHeight?: number;
	maxHeight?: number;
	activeColor?: string;
	inactiveColor?: string;
	mode?: AmplitudeMode;
	isLoading?: boolean;
	index: number;
	activeBarIndex: SharedValue<number>;
};

const WaveformBar = memo(
	({
		amplitude,
		isPlaying,
		barWidth = BAR_WIDTH,
		barMargin = BAR_MARGIN,
		minHeight = MIN_HEIGHT,
		maxHeight = MAX_HEIGHT,
		activeColor,
		inactiveColor,
		mode = "default",
		isLoading = false,
		index,
		activeBarIndex,
	}: WaveformBarProps) => {
		const theme = useAppTheme();
		const heightShared = useSharedValue(0);
		const isActiveShared = useDerivedValue(() => index <= activeBarIndex.value);

		// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
		useEffect(() => {
			heightShared.value = minHeight + amplitude * (maxHeight - minHeight);
		}, [amplitude, minHeight, maxHeight]);

		const animatedStyle = useAnimatedStyle(() => {
			if (isLoading) {
				const rand1 = Math.random();
				const rand2 = Math.random();
				return {
					height: withRepeat(
						withSequence(
							withSpring(maxHeight * rand1, {
								damping: 10,
								stiffness: 40,
								mass: 0.5,
							}),
							withSpring(maxHeight * rand2, {
								damping: 10,
								stiffness: 40,
								mass: 0.5,
							}),
						),
						-1,
						true,
					),
					backgroundColor: theme.colors.surfaceDisabled,
					opacity: withSpring(0.4 + Math.random(), {
						damping: 15,
						stiffness: 40,
					}),
				};
			}

			if (isPlaying && isActiveShared.value && mode === "recorder") {
				return {
					height: withRepeat(
						withSequence(
							withSpring(heightShared.value * 1.2, {
								damping: 10,
								stiffness: 100,
								mass: 0.3,
							}),
							withSpring(heightShared.value * 0.9, {
								damping: 10,
								stiffness: 100,
								mass: 0.3,
							}),
						),
						-1,
						true,
					),
					backgroundColor: activeColor ?? theme.colors.info.main,
					opacity: 1,
				};
			}

			return {
				height: withSpring(heightShared.value, {
					damping: 15,
					stiffness: 40,
					mass: 0.5,
				}),
				backgroundColor: isActiveShared.value
					? (activeColor ?? theme.colors.info.main)
					: (inactiveColor ?? theme.colors.surfaceDisabled),
				opacity: 1,
			};
		}, [isPlaying, activeColor, inactiveColor, mode, isLoading]);

		const baseStyle = useMemo(
			() => ({
				width: barWidth,
				borderRadius: barWidth / 2,
				marginHorizontal: barMargin,
			}),
			[barWidth, barMargin],
		);

		return <Animated.View style={[baseStyle, animatedStyle]} />;
	},
);

export type WaveformVisualizerProps = {
	progress: number;
	isPlaying: boolean;
	uri?: string;
	barWidth?: number;
	barMargin?: number;
	minHeight?: number;
	maxHeight?: number;
	activeColor?: string;
	inactiveColor?: string;
	style?: React.ComponentProps<typeof Box>;
	meterHistory?: number[];
	mode?: AmplitudeMode;
	onSeek?: (progress: number) => Promise<void>;
	canSeek?: boolean;
	onAmplitudesLoading?: (isLoading: boolean) => void;
	waveformWidth?: number;
	updateWaveformWidth?: (width: number) => void;
	cursor?: React.ComponentProps<typeof Box>["cursor"];
	showWaveform?: boolean;
	useReanimated?: boolean;
};

export const WaveformVisualizer = ({
	progress,
	isPlaying,
	uri,
	barWidth = BAR_WIDTH,
	barMargin = BAR_MARGIN,
	minHeight = MIN_HEIGHT,
	maxHeight = MAX_HEIGHT,
	activeColor,
	inactiveColor,
	style,
	meterHistory = [],
	mode = "default",
	onSeek,
	canSeek = true,
	onAmplitudesLoading,
	waveformWidth,
	updateWaveformWidth,
	cursor = "grab",
	showWaveform = true,
	useReanimated = true,
}: WaveformVisualizerProps) => {
	const [containerWidth, setContainerWidth] = useState(waveformWidth || 0);
	const seekProgress = useSharedValue(progress);

	const barCount = useMemo(
		() => Math.floor(containerWidth / (barWidth + barMargin * 2)),
		[containerWidth, barWidth, barMargin],
	);

	useEffect(() => {
		if (updateWaveformWidth && waveformWidth === 0) {
			updateWaveformWidth(containerWidth);
		}
	}, [containerWidth, updateWaveformWidth, waveformWidth]);

	useEffect(() => {
		if (waveformWidth) {
			setContainerWidth(waveformWidth);
		}
	}, [waveformWidth]);

	const { amplitudes, isLoading: isAmplitudesLoading } = useAmplitudes({
		barCount,
		meterHistory,
		mode,
		uri,
		onAmplitudesLoading,
	});

	const activeBarIndex = useDerivedValue(() => {
		return Math.floor(seekProgress.value * amplitudes.length);
	});

	useEffect(() => {
		seekProgress.value = progress;
	}, [progress, seekProgress]);

	const onLayout = useCallback((event: LayoutChangeEvent) => {
		setContainerWidth((prev) => {
			if (event.nativeEvent?.layout?.width > 0) {
				return event.nativeEvent.layout.width;
			}
			return prev;
		});
	}, []);

	const handleSeek = useCallback(
		async (newProgress: number) => {
			if (onSeek) {
				await onSeek(newProgress);
			}
		},
		[onSeek],
	);

	const panGesture = useMemo(
		() =>
			Gesture.Pan()
				.enabled(canSeek && showWaveform)
				.onUpdate((e) => {
					const newProgress = Math.max(0, Math.min(1, e.x / containerWidth));
					seekProgress.value = newProgress;
					runOnJS(handleSeek)(newProgress);
				}),
		[canSeek, showWaveform, containerWidth, handleSeek, seekProgress],
	);
	const tapGesture = useMemo(
		() =>
			Gesture.Tap()
				.enabled(canSeek && showWaveform)
				.onEnd((e) => {
					const newProgress = Math.max(0, Math.min(1, e.x / containerWidth));
					seekProgress.value = newProgress;
					runOnJS(handleSeek)(newProgress);
				}),
		[canSeek, showWaveform, containerWidth, handleSeek, seekProgress],
	);

	const gesture = useMemo(
		() => Gesture.Race(panGesture, tapGesture),
		[panGesture, tapGesture],
	);

	const theme = useAppTheme();

	return (
		<GestureDetector gesture={gesture}>
			<Box
				flexDirection="row"
				alignItems="center"
				height={maxHeight * 1.5}
				flex={1}
				overflow="hidden"
				onLayout={onLayout}
				cursor={cursor}
				{...style}
			>
				{showWaveform ? (
					containerWidth > 0 ? (
						amplitudes.map((amplitude, index) =>
							useReanimated ? (
								<WaveformBar
									isLoading={isAmplitudesLoading}
									key={index.toString()}
									amplitude={amplitude}
									index={index}
									activeBarIndex={activeBarIndex}
									isPlaying={isPlaying}
									barWidth={barWidth}
									barMargin={barMargin}
									minHeight={minHeight}
									maxHeight={maxHeight}
									activeColor={activeColor}
									inactiveColor={inactiveColor}
									mode={mode}
								/>
							) : (
								<Box
									key={index.toString()}
									height={minHeight + amplitude * (maxHeight - minHeight)}
									width={barWidth}
									backgroundColor={
										activeBarIndex.value > index
											? (activeColor ?? theme.colors.info.main)
											: (inactiveColor ?? theme.colors.surfaceDisabled)
									}
									marginHorizontal={barMargin}
								/>
							),
						)
					) : (
						<TextSkeleton height={16} />
					)
				) : null}
			</Box>
		</GestureDetector>
	);
};
